import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isApiError } from './api';

/** Retry only transient failures: network errors, 5xx and 429 (with backoff). Never 4xx. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (!isApiError(error)) return failureCount < 1;
  if (error.code === 'NETWORK_ERROR') return true;
  if (error.status === 429) return failureCount < 1;
  return error.status >= 500;
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache(),
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 6000),
        staleTime: 20_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Query key factory — keep keys centralised so invalidation stays consistent. */
export const qk = {
  session: ['session'] as const,
  clients: (params: Record<string, unknown>) => ['clients', params] as const,
  clientsAll: ['clients'] as const,
  client: (id: string) => ['client', id] as const,
  profileRevisions: (id: string) => ['client', id, 'profile-revisions'] as const,
  staff: ['staff'] as const,
  packages: ['packages'] as const,
  templates: (status?: string) => ['templates', status ?? 'all'] as const,
  contactRoles: ['contact-roles'] as const,
  engagements: (params: Record<string, unknown>) => ['engagements', params] as const,
  engagementsAll: ['engagements'] as const,
  engagement: (id: string) => ['engagement', id] as const,
  timeline: (id: string) => ['engagement', id, 'timeline'] as const,
  tasks: (id: string) => ['engagement', id, 'tasks'] as const,
  comments: (id: string, audience?: string) => ['engagement', id, 'comments', audience ?? 'all'] as const,
  waivers: (id: string) => ['engagement', id, 'waivers'] as const,
  invitations: (id: string) => ['engagement', id, 'invitations'] as const,
  approvals: (id: string) => ['engagement', id, 'approvals'] as const,
  notifications: ['notifications'] as const,
};
